import * as THREE from 'three';

/**
 * One material policy for every Meshy model (docs/research/MESHY-WORLD-PLAN.md, P4 and §4.3): the city's
 * material model (no metal, rough, low environment), sRGB colour, and the mip policy — mips for a packed
 * atlas (street pack, interiors), none for an auto-UV character atlas whose islands bleed at low mips.
 * Shader patches (occluder fade, knock-downs, the street atlas) are applied by the views that own them.
 */
export interface MeshyTune {
  roughness?: number;
  metalness?: number;
  envMapIntensity?: number;
  /** Mipmaps on the colour texture (default true: packed atlases). */
  mips?: boolean;
  /** Multiply the vertex colours in (contact shading, per-instance tint). */
  vertexColors?: boolean;
}

export const MESHY_DEFAULTS: Required<MeshyTune> = { roughness: 0.88, metalness: 0, envMapIntensity: 0.7, mips: true, vertexColors: false };

/** Tune a material in place (and its texture) and return it. */
export function tuneMeshyMaterial<M extends THREE.MeshStandardMaterial>(mat: M, opts: MeshyTune = {}): M {
  const o = { ...MESHY_DEFAULTS, ...opts };
  mat.roughness = o.roughness;
  mat.metalness = o.metalness;
  mat.envMapIntensity = o.envMapIntensity;
  mat.vertexColors = o.vertexColors;
  // the build drops these: a stray one from a hand-made file would light the model like a photo
  mat.metalnessMap = null;
  mat.roughnessMap = null;
  mat.aoMap = null;
  mat.emissiveMap = null;
  if (mat.map) tuneMeshyTexture(mat.map, o.mips);
  mat.needsUpdate = true;
  return mat;
}

/** sRGB and the mip policy for a Meshy colour texture. */
export function tuneMeshyTexture(t: THREE.Texture, mips = true): THREE.Texture {
  t.colorSpace = THREE.SRGBColorSpace;
  // A KTX2 texture (render/ktx2.ts) brings its own mip chain: the GPU cannot generate one for a compressed format.
  t.generateMipmaps = mips && !(t as THREE.CompressedTexture).isCompressedTexture;
  t.minFilter = mips ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}
